import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Bot, Clock3, LoaderCircle, Plus, Send, X } from 'lucide-react';
import { STAFF_API } from '../staffAuth';
import OwnerAssistantProposal from './OwnerAssistantProposal';
import './OwnerAssistant.css';

const API = `${STAFF_API}/api/staff/owner-assistant`;
const readableRunError = (message) => /ENOTFOUND|EAI_AGAIN|GEMINI_DNS_UNAVAILABLE/i.test(message || '')
  ? 'Serwer nie może odnaleźć adresu Gemini (problem DNS na VPS). Sprawdź połączenie serwera i ponów.'
  : /ECONNRESET|ETIMEDOUT|EHOSTUNREACH|GEMINI_CONNECTION_UNAVAILABLE/i.test(message || '')
    ? 'Serwer nie może połączyć się z Gemini. Sprawdź połączenie VPS i ponów.'
    : message || 'Zadanie się nie powiodło';

export default function OwnerAssistant({ open, onClose, routeId, recordId }) {
  const [threads, setThreads] = useState([]);
  const [threadId, setThreadId] = useState(() => localStorage.getItem('vroom-owner-assistant-thread') || null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [schedulePrompt, setSchedulePrompt] = useState('');
  const [scheduleTime, setScheduleTime] = useState('08:30');
  const bottom = useRef(null);
  const sendLock = useRef(false);

  const loadThreads = useCallback(async () => {
    const { data } = await axios.get(`${API}/threads`);
    setThreads(data.threads || []);
  }, []);
  const loadThread = useCallback(async (id) => {
    if (!id) return;
    const { data } = await axios.get(`${API}/threads/${id}`);
    setThread(data.thread);
  }, []);
  const loadSchedules = useCallback(async () => { const { data } = await axios.get(`${API}/schedules`); setSchedules(data.schedules || []); }, []);
  useEffect(() => { if (open) { loadThreads().catch(() => setError('Nie udało się wczytać rozmów.')); loadSchedules().catch(() => {}); } }, [open, loadThreads, loadSchedules]);
  useEffect(() => {
    if (!open) return undefined;
    const timer = setInterval(() => loadThreads().catch(() => {}), 30000);
    return () => clearInterval(timer);
  }, [open, loadThreads]);
  useEffect(() => { if (threadId) localStorage.setItem('vroom-owner-assistant-thread', threadId); else localStorage.removeItem('vroom-owner-assistant-thread'); }, [threadId]);
  useEffect(() => {
    if (!open || !threadId) return undefined;
    loadThread(threadId).catch(() => setError('Nie udało się wczytać rozmowy.'));
    const timer = setInterval(() => loadThread(threadId).catch(() => {}), 2500);
    return () => clearInterval(timer);
  }, [open, threadId, loadThread]);
  useEffect(() => { bottom.current?.scrollIntoView({ block: 'end', behavior: 'smooth' }); }, [thread?.messages?.length, thread?.runs?.[0]?.status, open]);

  const send = async (event) => {
    event.preventDefault();
    const prompt = text.trim();
    if (!prompt || sendLock.current) return;
    sendLock.current = true;
    setSending(true); setError(''); setText('');
    try {
      const { data } = await axios.post(`${API}/runs`, { prompt, routeId, recordId, threadId });
      setThreadId(data.threadId);
      await Promise.all([loadThread(data.threadId), loadThreads()]);
    } catch (e) { setText(prompt); setError(e.response?.data?.message || 'Nie udało się wysłać wiadomości.'); }
    finally { sendLock.current = false; setSending(false); }
  };
  const approve = async (proposal, actions) => {
    setError('');
    await axios.post(`${API}/proposals/${proposal.id}/approve`, { actions });
    await loadThread(threadId);
  };
  const reject = async (proposal) => {
    setError('');
    await axios.post(`${API}/proposals/${proposal.id}/reject`);
    await loadThread(threadId);
  };
  const correct = () => {
    setText('Popraw propozycję: ');
    document.querySelector('.oa-compose textarea')?.focus();
  };
  const resume = async (run) => {
    try { await axios.post(`${API}/runs/${run.id}/resume`); await loadThread(threadId); }
    catch (e) { setError(e.response?.data?.message || 'Nie udało się wznowić zadania.'); }
  };
  const saveSchedule = async (event) => {
    event.preventDefault(); setError('');
    try { await axios.post(`${API}/schedules`, { prompt: schedulePrompt, time: scheduleTime }); setSchedulePrompt(''); await loadSchedules(); }
    catch (e) { setError(e.response?.data?.message || 'Nie udało się zapisać harmonogramu.'); }
  };
  if (!open) return null;
  const timeline = [
    ...(thread?.messages || []).map((message) => ({ kind: 'message', item: message, at: message.createdAt })),
    ...(thread?.runs || []).map((run) => ({ kind: 'run', item: run, at: run.completedAt || run.createdAt })),
  ].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  const activeRun = thread?.runs?.some((run) => run.status === 'queued' || run.status === 'running');
  return <div className="oa-scrim" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="oa-panel" role="dialog" aria-modal="true" aria-label="Asystent VROOM">
      <header className="oa-header"><div className="oa-avatar"><Bot size={22} /></div><div><strong>Asystent VROOM</strong><small>Aktualna zakładka: {routeId}</small></div><button type="button" className="oa-icon" onClick={onClose} aria-label="Zamknij"><X size={20} /></button></header>
      <div className="oa-toolbar"><select aria-label="Wybierz rozmowę" value={threadId || ''} onChange={(e) => { setThreadId(e.target.value || null); setThread(null); }}><option value="">Nowa rozmowa</option>{threads.map((item) => <option key={item.id} value={item.id}>{item.title || 'Rozmowa'} · {new Date(item.updatedAt).toLocaleDateString('pl-PL')}</option>)}</select><button type="button" className="oa-icon" title="Cykliczne przeglądy" onClick={() => setScheduleOpen((value) => !value)}><Clock3 size={20} /></button><button type="button" className="oa-icon" title="Nowa rozmowa" onClick={() => { setThreadId(null); setThread(null); }}><Plus size={20} /></button></div>
      {scheduleOpen && <section className="oa-schedules"><form onSubmit={saveSchedule}><input aria-label="Zadanie cykliczne" placeholder="Co sprawdzać codziennie?" value={schedulePrompt} onChange={(e) => setSchedulePrompt(e.target.value)} required /><input aria-label="Godzina przeglądu" type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} required /><button type="submit">Dodaj</button></form>{schedules.map((schedule) => <label key={schedule.id}><input type="checkbox" checked={schedule.enabled} onChange={async (e) => { try { await axios.patch(`${API}/schedules/${schedule.id}`, { enabled: e.target.checked }); await loadSchedules(); } catch { setError('Nie udało się zmienić harmonogramu.'); } }} /> {schedule.cronSpec} · {schedule.prompt}</label>)}</section>}
      <div className="oa-timeline" aria-live="polite">
        {!thread && <div className="oa-empty"><Bot size={30} /><h2>W czym mogę pomóc?</h2><p>Sprawdzę dane w panelu, przygotuję szkic lub pokażę działania do zatwierdzenia.</p></div>}
        {timeline.map(({ kind, item }) => {
          if (kind === 'message') return <div key={`message-${item.id}`} className={`oa-message oa-message--${item.role}`}><span>{item.role === 'user' ? 'Ty' : 'Asystent'}</span><div>{item.content}</div>{item.data?.drafts?.map((draft, index) => <aside className="oa-draft" key={index}><b>{draft.error ? 'Nie udało się' : 'Szkic'}</b><pre>{JSON.stringify(draft.error || draft.data || draft.result, null, 2)}</pre></aside>)}</div>;
          const run = item;
          return <React.Fragment key={`run-${run.id}`}>
          {['queued', 'running'].includes(run.status) && <div className="oa-progress"><LoaderCircle className="oa-spin" size={17} /> {run.status === 'queued' ? 'Czekam na rozpoczęcie…' : ({ reading: 'Odczytuję dane zakładki…', thinking: 'Analizuję dane i piszę odpowiedź…', preparing: 'Przygotowuję szkice i podgląd działań…' }[run.context?.stage] || 'Pracuję nad odpowiedzią…')}</div>}
          {run.status === 'failed' && <div className="oa-error">{readableRunError(run.error)} <button type="button" onClick={() => resume(run)}>Ponów</button></div>}
          {run.status === 'awaiting_owner_session' && <div className="oa-progress"><Clock3 size={17} /> Zaplanowany przegląd czeka na uruchomienie <button type="button" onClick={() => resume(run)}>Uruchom</button></div>}
          {run.proposals?.map((proposal) => <OwnerAssistantProposal key={proposal.id} proposal={proposal} onApprove={approve} onReject={reject} onCorrection={correct} />)}
        </React.Fragment>;
        })}
        <div ref={bottom} />
      </div>
      {error && <div className="oa-error" role="alert">{error}</div>}
      <form className="oa-compose" onSubmit={send}><textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={activeRun ? 'Poczekaj na odpowiedź…' : 'Napisz, co mam sprawdzić lub przygotować…'} disabled={activeRun} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); e.currentTarget.form.requestSubmit(); } }} rows={2} /><button type="submit" disabled={!text.trim() || sending || activeRun} aria-label="Wyślij wiadomość">{sending || activeRun ? <LoaderCircle className="oa-spin" size={18} /> : <Send size={18} />}</button></form>
      <footer>Odczyty i szkice wykonuję sam. Wysyłka, Premium i zmiany wymagają Twojego zatwierdzenia.</footer>
    </section>
  </div>;
}
